import { useAuthStore } from '@/store/auth-store';
import { sessionScope } from '@/features/auth/session-scope';
import { normalizePreference, type LanguagePreference } from '@/lib/i18n/locales';
import { getLocaleSnapshot, setLanguagePreference } from '@/lib/i18n/runtime';

function owner() { return useAuthStore.getState().user?.id ?? 'anonymous'; }
function storageKey(account: string) { return `blaze-language:${account}`; }
const publicKey = 'blaze-language:public';
const publicScopes = new Set<symbol>();
const publicListeners = new Set<() => void>();

// Adopted public surfaces use a browser preference independent of any agent.
export function isPublicViewerLocation(path = typeof window === 'undefined' ? '' : window.location.pathname) {
  return /^\/(?:csat|meet)\/[^/]+\/?$/.test(path);
}
export const isPublicViewer = () => publicScopes.size > 0 || isPublicViewerLocation();
export function subscribePublicViewer(listener: () => void) {
  publicListeners.add(listener);
  return () => { publicListeners.delete(listener); };
}
function readPublic(): LanguagePreference {
  try { return normalizePreference(localStorage.getItem(publicKey)); }
  catch { return 'auto'; }
}
function viewPreference() { return isPublicViewer() ? readPublic() : read(owner()); }

export async function applyPublicPreference(preference: LanguagePreference) {
  const value = normalizePreference(preference);
  try { localStorage.setItem(publicKey, value); } catch { /* Browser session still works. */ }
  return isPublicViewer() ? setLanguagePreference(value) : false;
}
export function retainPublicViewer() {
  const scope = Symbol('public-viewer');
  publicScopes.add(scope);
  publicListeners.forEach(listener => listener());
  void setLanguagePreference(readPublic());
  return () => {
    publicScopes.delete(scope);
    publicListeners.forEach(listener => listener());
    // Wait for namespace cleanup and avoid an old release overriding a new view.
    queueMicrotask(() => { if (!isPublicViewer()) void setLanguagePreference(read(owner())); });
  };
}

function read(account: string): LanguagePreference {
  try {
    const saved = localStorage.getItem(storageKey(account));
    if (saved !== null) return normalizePreference(saved);
  } catch { /* Storage may be unavailable in private/embedded contexts. */ }
  return normalizePreference(account === 'anonymous' ? 'auto' : useAuthStore.getState().user?.language);
}

/** A per-account cache paints reloads; /auth/me remains authoritative. */
export async function applyAccountPreference(account: string, preference: LanguagePreference) {
  if (owner() !== account) return false;
  try { localStorage.setItem(storageKey(account), preference); } catch { /* in-memory session works */ }
  return isPublicViewer() ? true : setLanguagePreference(preference);
}

export async function initializeLocaleSession() {
  await setLanguagePreference(viewPreference());
  useAuthStore.subscribe((next, previous) => {
    if (sessionScope(next) !== sessionScope(previous)) {
      if (!isPublicViewer()) void setLanguagePreference(read(owner()));
    }
  });
  window.addEventListener('storage', (event) => {
    if (event.key === (isPublicViewer() ? publicKey : storageKey(owner()))) {
      void setLanguagePreference(normalizePreference(event.newValue));
    }
  });
  window.addEventListener('languagechange', () => {
    if (getLocaleSnapshot().preference === 'auto') void setLanguagePreference('auto');
  });
}
